import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { environment } from 'src/environments/environment';

/**
 * HTTP Error Interceptor
 * 
 * Intercepts HTTP errors (4xx, 5xx) and logs them to the centralized
 * logging service. This interceptor:
 * - Captures failed API requests
 * - Logs request URL, status code, and error message
 * - Skips logging for the logging API itself (prevents infinite loops)
 * - Does NOT log request/response payloads (HIPAA compliance)
 * 
 * Note: This interceptor should be registered AFTER AuthInterceptor
 * to ensure it can access the full request context.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private logger: LoggerService | null = null;

  constructor(private injector: Injector) {
    // Use Injector to avoid circular dependency issues
  }

  /**
   * Get or initialize LoggerService
   * Returns null if service cannot be obtained (never throws)
   */
  private getLoggerService(): LoggerService | null {
    try {
      if (!this.logger) {
        try {
          this.logger = this.injector.get(LoggerService);
        } catch (injectorError) {
          // If LoggerService cannot be injected, return null
          // This should never break the flow
          if (!environment.production) {
            console.warn('HttpErrorInterceptor: Could not inject LoggerService:', injectorError);
          }
          return null;
        }
      }
      return this.logger;
    } catch (error) {
      // Any other error - return null to prevent breaking the flow
      if (!environment.production) {
        console.warn('HttpErrorInterceptor: Error getting LoggerService:', error);
      }
      return null;
    }
  }

  /**
   * Check if the request is to the logging API itself
   * Gets the endpoint from LoggerService to avoid dependency on environment
   */
  private isLoggingApiRequest(url: string): boolean {
    if (!url) {
      return false;
    }
    
    try {
      const logger = this.getLoggerService();
      if (!logger) {
        return false;
      }
      
      // Get the logging API endpoint from LoggerService
      // We need to check if the URL contains the logging API path
      // Since getLogApiEndpoint is private, we check for the pattern
      const loggingApiPath = 'api/audit/clientlogs';
      
      try {
        const requestUrl = new URL(url, window.location.origin);
        // Check if the pathname includes the logging API path
        return requestUrl.pathname.includes(loggingApiPath);
      } catch (urlError) {
        // If URL parsing fails, do simple string comparison
        return url.includes(loggingApiPath);
      }
    } catch (error) {
      // If any error occurs, return false to be safe
      return false;
    }
  }

  /**
   * Extract error message
   */
  private extractSafeErrorMessage(error: HttpErrorResponse): string {
    let message = error.message || `HTTP ${error.status} ${error.statusText}`;
    
    if (error.status === 0) {
      message = 'Network error: Unable to connect to server';
    } else if (error.status >= 400 && error.status < 500) {
      message = `Client error: ${error.status} ${error.statusText}`;
    } else if (error.status >= 500) {
      message = `Server error: ${error.status} ${error.statusText}`;
    }

    return message;
  }

  /**
   * Intercept HTTP requests and handle errors
   * 
   * CRITICAL: This method MUST NEVER break the HTTP request/response flow.
   * Even if logging fails completely, the original error must always be re-thrown
   * so that the application's normal error handling can proceed.
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Wrap entire intercept logic in try-catch to ensure flow never breaks
    try {
      return next.handle(req).pipe(
        catchError((error: HttpErrorResponse) => {
          // CRITICAL: Always re-throw the error at the end, regardless of what happens in between
          // This ensures the application's error handling flow is never broken
          
          try {
            // Skip logging for the logging API itself to prevent infinite loops
            if (this.isLoggingApiRequest(req.url)) {
              return throwError(() => error);
            }

            // Only log 4xx and 5xx errors (client and server errors)
            if (error && error.status >= 400) {
              try {
                const logger = this.getLoggerService();
                
                // Only attempt logging if service is available
                if (logger) {
                  try {
                    const safeMessage = this.extractSafeErrorMessage(error);
                    
                    // Extract module name from URL or use default
                    let moduleName = 'HttpInterceptor';
                    try {
                      if (req.url) {
                        const urlParts = new URL(req.url, window.location.origin);
                        const pathParts = urlParts.pathname.split('/').filter(p => p);
                        if (pathParts.length > 0) {
                          moduleName = pathParts[pathParts.length - 1] || 'HttpInterceptor';
                        }
                      }
                    } catch (urlError) {
                      // If URL parsing fails, use default - don't break flow
                      moduleName = 'HttpInterceptor';
                    }

                    // Log the HTTP error - wrapped in try-catch to prevent any errors from breaking flow
                    try {
                      logger.logHttpError(
                        req.url || 'Unknown URL',
                        error.status || 0,
                        error.statusText || 'Unknown',
                        safeMessage,
                        moduleName,
                        {
                          method: req.method || 'Unknown'
                        }
                      );
                    } catch (logError) {
                      // If logHttpError itself throws, catch it and continue
                      // This should never happen, but we're being extra safe
                      if (!environment.production) {
                        console.warn('HttpErrorInterceptor: logHttpError threw an error:', logError);
                      }
                    }
                  } catch (loggingError) {
                    // If any part of logging fails, silently continue
                    // Only log to console in development
                    if (!environment.production) {
                      console.warn('HttpErrorInterceptor: Failed to log error:', loggingError);
                    }
                  }
                }
              } catch (serviceError) {
                // If getting or using the service fails, silently continue
                // This should never happen, but we're being extra safe
                if (!environment.production) {
                  console.warn('HttpErrorInterceptor: Service error:', serviceError);
                }
              }
            }
          } catch (interceptorError) {
            // If ANYTHING in the interceptor fails, catch it and continue
            // The error must still be re-thrown so the application flow continues
            if (!environment.production) {
              console.error('HttpErrorInterceptor: Unexpected error in interceptor:', interceptorError);
            }
          }

          // CRITICAL: Always re-throw the original error
          // This ensures the application's error handling flow is never broken
          return throwError(() => error);
        })
      );
    } catch (outerError) {
      // If even the pipe setup fails (should never happen), still handle the request
      // and return the error
      if (!environment.production) {
        console.error('HttpErrorInterceptor: Critical error in intercept method:', outerError);
      }
      // Fallback: handle request and catch errors
      return next.handle(req).pipe(
        catchError((error: HttpErrorResponse) => {
          // Always re-throw the error
          return throwError(() => error);
        })
      );
    }
  }
}


